import { CardGrid } from '../../components/CardGrid';
import { ContentArea } from '../../components/ContentArea';
import { EmptyMessage } from '../../components/EmptyMessage';
import { ItemCard } from '../../components/ItemCard';
import { Section, Title, TitleRow } from '../../components/Section';
import { SkeletonCard } from '../../components/SkeletonCard';
import type {
  AccommodationListItem,
  AccommodationListTemplateProps,
} from './AccommodationListTemplate.types';

const META_SEPARATOR = ' · ';

function buildMeta(item: AccommodationListItem): string | undefined {
  const labels = item.occupancyLabels?.filter(Boolean) ?? [];
  return labels.length > 0 ? labels.join(META_SEPARATOR) : undefined;
}

export function AccommodationListTemplate({
  items,
  onSelectItem,
  isLoading,
  titleLabel,
  fromPriceLabel,
  soldOutLabel,
  noResultsLabel,
  noImageLabel,
  editSearchLabel,
  onEditSearch,
  columns = 2,
  imageAspectRatio = '3 / 2',
}: AccommodationListTemplateProps) {
  if (isLoading && items.length === 0) {
    return (
      <Section>
        <TitleRow>
          <Title>{titleLabel}</Title>
        </TitleRow>
        <CardGrid $columns={columns}>
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonCard key={index} variant="grid" />
          ))}
        </CardGrid>
      </Section>
    );
  }

  if (!isLoading && items.length === 0) {
    return (
      <Section>
        <TitleRow>
          <Title>{titleLabel}</Title>
        </TitleRow>
        <EmptyMessage>{noResultsLabel}</EmptyMessage>
      </Section>
    );
  }

  return (
    <Section>
      <TitleRow>
        <Title>{titleLabel}</Title>
      </TitleRow>
      <ContentArea>
        <CardGrid $columns={columns}>
          {items.map((item) => (
            <ItemCard
              key={item.id}
              image={{ src: item.imageUrl, alt: item.name, placeholderLabel: noImageLabel }}
              imageAspectRatio={imageAspectRatio}
              title={item.name}
              meta={buildMeta(item)}
              imageBadge={item.promotionName || undefined}
              unavailableLabel={
                item.unavailable ? item.unavailableLabel ?? soldOutLabel : undefined
              }
              editSearchLabel={editSearchLabel}
              onEditSearch={onEditSearch}
              price={
                !item.unavailable && item.price
                  ? {
                      amount: item.price.amount,
                      currencyCode: item.price.currencyCode,
                      label: fromPriceLabel,
                      discount: item.discount,
                    }
                  : undefined
              }
              unavailable={item.unavailable}
              ariaLabel={item.name}
              onSelect={() => onSelectItem(item.id)}
            />
          ))}
        </CardGrid>
      </ContentArea>
    </Section>
  );
}
